import { Children, cloneElement, isValidElement, useId, useState } from 'react'
import type { ReactNode } from 'react'

export interface TooltipProps {
  readonly label: string
  readonly children: ReactNode
  /** Position relative to the trigger; the collapsed sidebar always wants
   * "right" (interactions.md §8). */
  readonly placement?: 'right' | 'top'
}

/**
 * Hover/focus-triggered tooltip (interactions.md §8 — replaces native
 * `title` attribute tooltips, which are slow-appearing and inaccessible to
 * touch/keyboard-only users, with one consistent mechanism). Used for the
 * collapsed sidebar's icon-rail nav items and workspace avatars (the
 * source's `fx-sbtip`/`fx-sbtip-label` pattern), extended here to every nav
 * item rather than mixing native-title and custom tooltips.
 *
 * Shows on `:hover` and `:focus-visible` (via onFocus/onBlur, since the
 * trigger itself is the focusable element — no separate tabbable target).
 *
 * `aria-describedby` must land on the focusable element itself, not a
 * wrapper `<span>` around it (UID-29) — a screen reader only reads the
 * description of whichever element actually receives focus. Every current
 * consumer (Sidebar's collapsed-rail nav `<Link>`s and workspace-avatar
 * buttons) passes a single focusable element as `children`, so we clone it
 * to inject the prop rather than wrapping it in a non-focusable span.
 */
export function Tooltip({ label, children, placement = 'right' }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const tooltipId = useId()

  const onlyChild = Children.only(children)
  const describedChild = isValidElement<Record<string, unknown>>(onlyChild)
    ? cloneElement(onlyChild, {
        'aria-describedby': visible ? tooltipId : undefined,
        onMouseEnter: chain(onlyChild.props.onMouseEnter, () => setVisible(true)),
        onMouseLeave: chain(onlyChild.props.onMouseLeave, () => setVisible(false)),
        onFocus: chain(onlyChild.props.onFocus, () => setVisible(true)),
        onBlur: chain(onlyChild.props.onBlur, () => setVisible(false)),
      })
    : onlyChild

  return (
    <span className="tooltip-wrapper">
      {describedChild}
      {visible && (
        <span role="tooltip" id={tooltipId} className={`tooltip tooltip-${placement}`}>
          {label}
        </span>
      )}
    </span>
  )
}

/** Calls the child's own handler (if any) before ours, so cloning never
 * silently drops a consumer-supplied event handler. */
function chain(childHandler: unknown, ours: () => void) {
  return (event: unknown) => {
    if (typeof childHandler === 'function') {
      childHandler(event)
    }
    ours()
  }
}
